import { useEffect, useState } from 'react';
import { DatabaseOutlined, InfoCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Layout, PageHeader, Collapse, Space, Typography, Tooltip, Spin } from 'antd';
import { apiClient, apiErrorMessage, parseHyperlink } from '../../utils';
import { Grid, GridGroup, GridStats } from '../../models';

import './grids-page.scoped.scss';
import { useHistory } from 'react-router-dom';

export function GridsPage() {
  const { Content } = Layout;

  const [loading, setLoading] = useState(false);
  const [grids, setGrids] = useState<(Grid & { key: string })[]>();
  const [gridGroups, setGridGroups] = useState<GridGroup[]>();
  const [scheme, setScheme] = useState<any[]>([]);

  const history = useHistory();

  useEffect(() => {
    loadGrids();
    loadGridGroups();
  }, []);

  const loadGrids = () => {
    setLoading(true);
    apiClient
      .get('grids')
      .then(response => {
        const responseGrids: Grid[] = response.data;
        const responseData = responseGrids.map(grid => {
          return {
            key: grid.id,
            ...grid,
          };
        });
        setGrids(responseData);
        setLoading(false);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
        setLoading(false);
      });
  };

  const loadGridGroups = () => {
    apiClient
      .get('grid-groups')
      .then(response => {
        setGridGroups(response.data);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      });
  };

  useEffect(() => {
    const newScheme: any[] = [];
    if (gridGroups?.length) {
      gridGroups.forEach(gg => {
        newScheme.push({ id: gg.id, itemType: 'gridGroup', children: [], order: gg.order });
      });
    }

    if (grids?.length) {
      grids.forEach(g => {
        if (!g.groupId) {
          newScheme.push({ id: g.id, itemType: 'grid', order: g.order });
        } else {
          const groupIndex = newScheme.findIndex(s => s.id === g.groupId);
          if (groupIndex !== -1) {
            newScheme[groupIndex].children.push({ id: g.id, itemType: 'grid', order: g.order });
          }
        }
      });
    }

    newScheme.sort((a, b) => a.order - b.order);
    newScheme.forEach(s => {
      if (s.children) {
        s.children.sort((a: any, b: any) => a.order - b.order);
      }
    });

    const filteredScheme = newScheme.filter(s => (s.itemType === 'gridGroup' && s.children.length > 0) || s.itemType === 'grid');

    setScheme(filteredScheme);
  }, [grids, gridGroups]);

  const onRefreshClick = () => {
    loadGrids();
  };

  return (
    <>
      <PageHeader
        title={
          <>
            <DatabaseOutlined /> Veri setleri
          </>
        }
        extra={[
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
        ]}
      />
      <Content id="itemsContent" style={{ background: '#FFFFFF', marginLeft: '1rem', marginRight: '1rem' }}>
        <Spin spinning={loading} style={{ width: '100%', paddingTop: '5rem' }}>
          <ItemsPanel
            grids={grids}
            gridGroups={gridGroups}
            scheme={scheme}
            onGridNameClick={id => {
              history.push('/grids/' + id);
            }}
          />
        </Spin>
      </Content>
    </>
  );
}

function ItemsPanel(props: {
  grids?: Grid[];
  gridGroups?: GridGroup[];
  onGridNameClick: (id: string) => void;
  scheme: any[];
  level?: string;
}) {
  const Item = ({ item }: any) => {
    if (item.itemType === 'grid') {
      const grid = props.grids?.find(g => g.id === item.id);
      if (grid) {
        return GridTr({
          grid,
          onGridNameClick: () => props.onGridNameClick(grid.id),
          id: grid.id,
        });
      }
    } else if (item.itemType === 'gridGroup') {
      const gridGroup = props.gridGroups?.find(g => g.id === item.id);

      if (gridGroup) {
        return GridGroupTr({
          gridGroup: props.gridGroups?.find(g => g.id === item.id) as GridGroup,
          onGridNameClick: props.onGridNameClick,
          grids: props.grids?.filter(g => g.groupId === item.id) || [],
          scheme: props.scheme,
          level: item.id,
        });
      }
    }
    return <div></div>;
  };

  return (
    <table>
      <tbody>
        {props.scheme?.map((item: any, i: number) => (
          <Item key={`item-${item.id}`} index={i} item={item} />
        ))}
      </tbody>
    </table>
  );
}

function GridGroupTr(props: {
  gridGroup: GridGroup;
  onGridNameClick: (id: string) => void;
  grids: Grid[];
  scheme: any[];
  level: string;
  stats?: GridStats[];
}) {
  const { Text } = Typography;

  const [isActive, setIsActive] = useState(true);
  const [data, setData] = useState(props.grids.sort((a, b) => a.order - b.order));

  const onChange = () => {
    setIsActive(!isActive);
  };

  useEffect(() => {
    setData(props.grids.sort((a, b) => a.order - b.order));
  }, [props.grids]);

  return (
    <tr key={props.gridGroup.id}>
      <td colSpan={2} style={{ width: '10000rem' }}>
        <Collapse
          key={props.gridGroup.id + '-collapse'}
          activeKey={isActive ? props.gridGroup.id : undefined}
          onChange={onChange}
          style={{ borderColor: props.gridGroup.hexCodeBackground }}
        >
          <Collapse.Panel
            style={{ backgroundColor: props.gridGroup.hexCodeBackground, borderColor: props.gridGroup.hexCodeBackground }}
            header={
              <Space>
                <Button type="link" style={{ padding: 0, height: 'auto', border: 0, fontWeight: 700, color: props.gridGroup.hexCodeTitle }}>
                  {props.gridGroup.name}
                </Button>
                <Tooltip title={props.gridGroup.description}>
                  <Text type="secondary">
                    <InfoCircleOutlined />
                  </Text>
                </Tooltip>
              </Space>
            }
            key={props.gridGroup.id}
          >
            <table>
              <tbody>
                {data?.map((item: any, i: number) => (
                  <GridTr key={item.id} grid={item} onGridNameClick={props.onGridNameClick} id={item.id} />
                ))}
              </tbody>
            </table>
          </Collapse.Panel>
        </Collapse>
      </td>
    </tr>
  );
}

function GridTr(props: {
  grid: Grid;
  tableActions?: (id: string) => any;
  onGridNameClick: (id: string) => void;
  id: string;
}) {
  const { Text } = Typography;

  return (
    <tr key={props.grid.id}>
      <td colSpan={1} style={{ width: '10000rem' }}>
        <div style={{ paddingLeft: '2.5rem' }}>
          <Space>
            <Button
              type="link"
              style={{ padding: 0, height: 'auto', border: 0, fontWeight: 600 }}
              onClick={() => props.onGridNameClick(props.grid.id)}
            >
              {props.grid.name}
            </Button>
            <Tooltip title={parseHyperlink(props.grid.description)}>
              <Text type="secondary">
                <InfoCircleOutlined />
              </Text>
            </Tooltip>
          </Space>
        </div>
        <div style={{ paddingLeft: '2.5rem', fontSize: '0.8rem' }}>
          <Text type="secondary">{props.grid.description}</Text>
        </div>
      </td>
    </tr>
  );
}
